import { Color, TRANSPARENT, hexToColor } from '../core/types';
import { ExprValue, compileExpression, toNumber, truthy } from './Expressions';

export type VariableType =
  | 'string'
  | 'integer'
  | 'float'
  | 'boolean'
  | 'percentage'
  | 'duration'
  | 'timestamp'
  | 'array'
  | 'computed';

export type Align = 'left' | 'center' | 'right';

/** Colour rule: the first matching rule wins. */
export interface ColorRule {
  /** Inclusive lower bound (numeric variables). */
  min?: number;
  /** Inclusive upper bound (numeric variables). */
  max?: number;
  /** Exact string match (non-numeric variables). */
  equals?: string;
  /** Hex colour applied to the rendered value. */
  color: string;
}

export interface VariableDef {
  name: string;
  type: VariableType;
  description?: string;
  /** Value used when no binding supplies one. */
  defaultValue?: ExprValue;
  /** Value shown in the editor while designing. */
  previewValue?: ExprValue;
  /** Value used when the binding sends null/undefined. */
  fallback?: ExprValue;
  /** Safe expression, only for type === 'computed'. */
  expression?: string;
  /** Minimum rendered width in cells. */
  width?: number;
  align?: Align;
  /** Padding character, defaults to a space. */
  padChar?: string;
  /** Default formatter pipeline applied before width/align, e.g. "round:1". */
  format?: string;
  colorRules?: ColorRule[];
}

export type VariableValues = Record<string, ExprValue>;

/* ------------------------------------------------------------------ *
 * Formatters
 * ------------------------------------------------------------------ */

export interface FormatterSpec {
  name: string;
  args: string[];
}

export const FORMATTER_NAMES = [
  'upper',
  'lower',
  'capitalize',
  'trim',
  'truncate',
  'padLeft',
  'padRight',
  'pad',
  'center',
  'number',
  'round',
  'percentage',
  'duration',
  'bytes',
  'abbrev',
  'date',
  'time',
  'plural',
  'prefix',
  'suffix',
  'bar',
  'bool',
] as const;

export type FormatterName = (typeof FORMATTER_NAMES)[number];

/**
 * Applies one formatter. Every formatter is a pure string/number transform —
 * there is no user-supplied code path here by design.
 */
export function applyFormatter(value: ExprValue, spec: FormatterSpec): ExprValue {
  const [a0, a1, a2] = spec.args;
  const str = value === null || value === undefined ? '' : String(value);
  switch (spec.name) {
    case 'upper':
      return str.toUpperCase();
    case 'lower':
      return str.toLowerCase();
    case 'capitalize':
      return str.charAt(0).toUpperCase() + str.slice(1);
    case 'trim':
      return str.trim();
    case 'truncate': {
      const n = Math.max(0, int(a0, 10));
      const ellipsis = a1 ?? '…';
      return str.length <= n ? str : str.slice(0, Math.max(0, n - ellipsis.length)) + ellipsis;
    }
    case 'pad':
    case 'padLeft':
      return str.padStart(int(a0, 0), a1 || ' ');
    case 'padRight':
      return str.padEnd(int(a0, 0), a1 || ' ');
    case 'center': {
      const w = int(a0, 0);
      if (str.length >= w) return str;
      const total = w - str.length;
      const left = Math.floor(total / 2);
      const ch = a1 || ' ';
      return ch.repeat(left) + str + ch.repeat(total - left);
    }
    case 'number':
      return formatNumber(toNumber(value), int(a0, 0), a1 ?? ',');
    case 'round': {
      const d = int(a0, 0);
      const f = 10 ** d;
      return Math.round(toNumber(value) * f) / f;
    }
    case 'percentage': {
      const d = int(a0, 0);
      return `${(toNumber(value)).toFixed(d)}%`;
    }
    case 'duration':
      return formatDuration(toNumber(value), a0 as DurationStyle);
    case 'bytes':
      return formatBytes(toNumber(value), int(a0, 1));
    case 'abbrev':
      return abbreviateNumber(toNumber(value), int(a0, 1));
    case 'date':
      return formatTimestamp(value, patternOr(spec.args, 'YYYY-MM-DD'));
    case 'time':
      return formatTimestamp(value, patternOr(spec.args, 'HH:mm:ss'));
    case 'plural': {
      const n = toNumber(value);
      const one = a0 ?? '';
      const many = a1 ?? `${one}s`;
      return n === 1 ? one : many;
    }
    case 'prefix':
      return (a0 ?? '') + str;
    case 'suffix':
      return str + (a0 ?? '');
    case 'bar': {
      const width = int(a0, 10);
      const full = a1 || '█';
      const empty = a2 || '░';
      const pct = Math.max(0, Math.min(1, toNumber(value) / 100));
      const filled = Math.round(pct * width);
      return full.repeat(filled) + empty.repeat(Math.max(0, width - filled));
    }
    case 'bool':
      return truthy(value) ? a0 ?? 'true' : a1 ?? 'false';
    default:
      return value;
  }
}

/**
 * Reassembles a date or time pattern from the parsed arguments.
 *
 * A colon separates a formatter's arguments and also appears inside the pattern
 * itself, so `time:HH:mm:ss` parses as three arguments rather than one. Taking
 * only the first renders `HH` — the hour and nothing else — which is a silent
 * wrong answer rather than an error. Rejoining is unambiguous because these two
 * formatters take exactly one argument.
 */
function patternOr(args: string[], fallback: string): string {
  if (args.length === 0) return fallback;
  const joined = args.join(':');
  return joined.trim() === '' ? fallback : joined;
}

function int(v: string | undefined, fallback: number): number {
  if (v === undefined || v === '') return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
}

export function formatNumber(n: number, digits: number, separator: string): string {
  const fixed = n.toFixed(Math.max(0, digits));
  const [whole, frac] = fixed.split('.');
  const sign = whole.startsWith('-') ? '-' : '';
  const digitsOnly = sign ? whole.slice(1) : whole;
  const grouped = digitsOnly.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  return sign + grouped + (frac ? `.${frac}` : '');
}

export type DurationStyle = 'short' | 'long' | 'clock' | undefined;

/** Seconds -> human duration. `clock` gives HH:MM:SS. */
export function formatDuration(totalSeconds: number, style: DurationStyle = 'short'): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  if (style === 'clock') {
    const h = days * 24 + hours;
    return [h, minutes, seconds].map((v) => String(v).padStart(2, '0')).join(':');
  }
  const units: Array<[number, string, string]> = [
    [days, 'd', 'day'],
    [hours, 'h', 'hour'],
    [minutes, 'm', 'minute'],
    [seconds, 's', 'second'],
  ];
  const parts = units.filter(([v]) => v > 0).slice(0, 2);
  if (parts.length === 0) return style === 'long' ? '0 seconds' : '0s';
  return parts
    .map(([v, short, long]) => (style === 'long' ? `${v} ${long}${v === 1 ? '' : 's'}` : `${v}${short}`))
    .join(' ');
}

export function formatBytes(bytes: number, digits = 1): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let v = Math.abs(bytes);
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  const sign = bytes < 0 ? '-' : '';
  return `${sign}${i === 0 ? v.toFixed(0) : v.toFixed(digits)} ${units[i]}`;
}

export function abbreviateNumber(n: number, digits = 1): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs < 1000) return `${sign}${abs}`;
  const units = ['k', 'M', 'B', 'T'];
  let v = abs;
  let i = -1;
  while (v >= 1000 && i < units.length - 1) {
    v /= 1000;
    i += 1;
  }
  return `${sign}${v.toFixed(digits).replace(/\.0+$/, '')}${units[i]}`;
}

/** Minimal, dependency-free token formatter for timestamps. */
export function formatTimestamp(value: ExprValue, pattern: string): string {
  const raw = typeof value === 'number' ? value : Date.parse(String(value ?? ''));
  const ms = Number.isFinite(raw) ? (raw < 1e12 ? raw * 1000 : raw) : Date.now();
  const d = new Date(ms);
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  const map: Record<string, string> = {
    YYYY: String(d.getFullYear()),
    YY: pad(d.getFullYear() % 100),
    MM: pad(d.getMonth() + 1),
    DD: pad(d.getDate()),
    HH: pad(d.getHours()),
    mm: pad(d.getMinutes()),
    ss: pad(d.getSeconds()),
  };
  return pattern.replace(/YYYY|YY|MM|DD|HH|mm|ss/g, (t) => map[t] ?? t);
}

/* ------------------------------------------------------------------ *
 * Template parsing:  "Users: {{online | pad:4}}"
 * ------------------------------------------------------------------ */

export interface TemplateSegment {
  type: 'text' | 'variable';
  /** Literal text, or the variable name. */
  value: string;
  formatters?: FormatterSpec[];
  /** Full original source, e.g. "{{online | pad:4}}". */
  raw?: string;
}

const TEMPLATE_RE = /\{\{\s*([^}]*?)\s*\}\}/g;

export function parseTemplate(text: string): TemplateSegment[] {
  const out: TemplateSegment[] = [];
  let last = 0;
  TEMPLATE_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = TEMPLATE_RE.exec(text)) !== null) {
    if (m.index > last) out.push({ type: 'text', value: text.slice(last, m.index) });
    const parts = m[1].split('|').map((p) => p.trim());
    const name = parts.shift() ?? '';
    out.push({
      type: 'variable',
      value: name,
      formatters: parts.filter(Boolean).map(parseFormatterSpec),
      raw: m[0],
    });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ type: 'text', value: text.slice(last) });
  return out;
}

export function parseFormatterSpec(src: string): FormatterSpec {
  const [name, ...args] = src.split(':');
  return { name: name.trim(), args: args.map((a) => a.trim()) };
}

export function templateVariableNames(text: string): string[] {
  return parseTemplate(text)
    .filter((s) => s.type === 'variable')
    .map((s) => s.value);
}

/* ------------------------------------------------------------------ *
 * Registry
 * ------------------------------------------------------------------ */

export interface ResolvedVariable {
  name: string;
  raw: ExprValue;
  text: string;
  color: Color;
}

/**
 * Holds the project's variable definitions and turns a payload of runtime
 * values into display strings. Computed variables are resolved in dependency
 * order with cycle detection.
 */
export class VariableRegistry {
  private defs = new Map<string, VariableDef>();

  constructor(defs: readonly VariableDef[] = []) {
    defs.forEach((d) => this.defs.set(d.name, d));
  }

  list(): VariableDef[] {
    return [...this.defs.values()];
  }

  get(name: string): VariableDef | undefined {
    return this.defs.get(name);
  }

  has(name: string): boolean {
    return this.defs.has(name);
  }

  set(def: VariableDef): void {
    this.defs.set(def.name, def);
  }

  remove(name: string): void {
    this.defs.delete(name);
  }

  rename(from: string, to: string): void {
    const def = this.defs.get(from);
    if (!def || from === to || this.defs.has(to)) return;
    this.defs.delete(from);
    this.defs.set(to, { ...def, name: to });
  }

  /**
   * Builds the full value scope: supplied values win, then defaults, then
   * computed expressions evaluated in topological order.
   */
  resolveScope(supplied: VariableValues = {}, usePreview = false): VariableValues {
    const scope: VariableValues = {};
    for (const def of this.defs.values()) {
      if (def.type === 'computed') continue;
      const given = supplied[def.name];
      if (given !== undefined && given !== null) {
        scope[def.name] = given;
      } else if (usePreview && def.previewValue !== undefined) {
        scope[def.name] = def.previewValue;
      } else if (def.defaultValue !== undefined) {
        scope[def.name] = def.defaultValue;
      } else if (def.fallback !== undefined) {
        scope[def.name] = def.fallback;
      } else {
        scope[def.name] = defaultForType(def.type);
      }
    }
    // Any payload keys with no definition are still usable in expressions.
    for (const [k, v] of Object.entries(supplied)) {
      if (!(k in scope)) scope[k] = v;
    }

    const computed = [...this.defs.values()].filter((d) => d.type === 'computed');
    const resolving = new Set<string>();
    const done = new Set<string>();

    const resolve = (def: VariableDef): void => {
      if (done.has(def.name)) return;
      if (resolving.has(def.name)) {
        scope[def.name] = def.fallback ?? 0;
        done.add(def.name);
        return;
      }
      resolving.add(def.name);
      try {
        const expr = compileExpression(def.expression ?? '0');
        for (const dep of expr.dependencies) {
          const depDef = this.defs.get(dep);
          if (depDef && depDef.type === 'computed' && !done.has(dep)) resolve(depDef);
        }
        scope[def.name] = expr.evaluate(scope);
      } catch {
        scope[def.name] = def.fallback ?? 0;
      }
      resolving.delete(def.name);
      done.add(def.name);
    };

    computed.forEach(resolve);
    return scope;
  }

  /** Formats a single variable, applying its formatters, width and align. */
  format(name: string, scope: VariableValues, extra: FormatterSpec[] = []): ResolvedVariable {
    const def = this.defs.get(name);
    let value = scope[name];
    if ((value === undefined || value === null) && def) {
      value = def.fallback ?? def.defaultValue ?? defaultForType(def.type);
    }
    if (value === undefined || value === null) value = '';

    const raw = value;
    const pipeline: FormatterSpec[] = [];
    if (def?.format) def.format.split('|').map((s) => s.trim()).filter(Boolean).forEach((s) => pipeline.push(parseFormatterSpec(s)));
    pipeline.push(...extra);

    let out: ExprValue = value;
    if (def && !def.format && !extra.length) out = defaultRender(def, value);
    for (const spec of pipeline) out = applyFormatter(out, spec);

    let text = out === null || out === undefined ? '' : String(out);
    if (def?.width && def.width > 0) text = applyWidth(text, def.width, def.align ?? 'left', def.padChar ?? ' ');

    return { name, raw, text, color: def ? colorForValue(def, raw) : TRANSPARENT };
  }

  /** Renders a template string against a scope. */
  render(template: string, scope: VariableValues): string {
    return parseTemplate(template)
      .map((seg) =>
        seg.type === 'text' ? seg.value : this.format(seg.value, scope, seg.formatters ?? []).text,
      )
      .join('');
  }
}

export function applyWidth(text: string, width: number, align: Align, padChar: string): string {
  const chars = [...text];
  if (chars.length > width) return chars.slice(0, width).join('');
  const total = width - chars.length;
  const pad = padChar || ' ';
  if (align === 'right') return pad.repeat(total) + text;
  if (align === 'center') {
    const left = Math.floor(total / 2);
    return pad.repeat(left) + text + pad.repeat(total - left);
  }
  return text + pad.repeat(total);
}

function defaultRender(def: VariableDef, value: ExprValue): ExprValue {
  switch (def.type) {
    case 'integer':
      return String(Math.round(toNumber(value)));
    case 'float':
      return String(Math.round(toNumber(value) * 100) / 100);
    case 'percentage':
      return `${Math.round(toNumber(value))}%`;
    case 'duration':
      return formatDuration(toNumber(value));
    case 'timestamp':
      return formatTimestamp(value, 'YYYY-MM-DD HH:mm:ss');
    case 'boolean':
      return truthy(value) ? 'yes' : 'no';
    case 'array':
      return Array.isArray(value) ? (value as unknown[]).join(', ') : String(value ?? '');
    default:
      return value;
  }
}

export function colorForValue(def: VariableDef, value: ExprValue): Color {
  if (!def.colorRules?.length) return TRANSPARENT;
  const n = toNumber(value);
  const s = String(value ?? '');
  for (const rule of def.colorRules) {
    if (rule.equals !== undefined) {
      if (rule.equals === s) return hexToColor(rule.color);
      continue;
    }
    const minOk = rule.min === undefined || n >= rule.min;
    const maxOk = rule.max === undefined || n <= rule.max;
    if (minOk && maxOk) return hexToColor(rule.color);
  }
  return TRANSPARENT;
}

export function defaultForType(type: VariableType): ExprValue {
  switch (type) {
    case 'integer':
    case 'float':
    case 'percentage':
    case 'duration':
      return 0;
    case 'boolean':
      return false;
    case 'timestamp':
      return Date.now();
    default:
      return '';
  }
}
